import { useAuth } from '@clerk/clerk-react';
import {
  BulkUpdateSharedKnowledgeFilesInput,
  CreateSharedKnowledgeGroupInput,
  DeleteManySharedKnowledgeFilesInput,
  PatchSharedKnowledgeFileInput,
  SharedKnowledgeFileDto,
  SharedKnowledgeFileFilterParams,
  UpdateSharedKnowledgeGroupInput,
} from '@ditto/shared';
import { skipToken, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBaseUrl,
  useAuthenticatedClient,
  useProtectedQuery,
  useWorkspaceBaseUrl,
} from './useApi';

const queryKeys = {
  all: ['shared-knowledge-files'],
  allFiles: () => [...queryKeys.all, 'files'],
  files: (
    page: number,
    limit: number,
    filters?: SharedKnowledgeFileFilterParams
  ) => [
    ...queryKeys.allFiles(),
    page,
    limit,
    ...(filters ? ['filters', filters] : []),
  ],
  fileById: (fileId: string) => [...queryKeys.allFiles(), fileId],
  allGroups: () => [...queryKeys.all, 'groups'],
  groups: () => [...queryKeys.allGroups(), 'groups'],
  filesForCitation: () => [...queryKeys.all, 'files-for-citation'],
};

export const useSharedKnowledgeFilesApi = (
  page: number = 1,
  limit: number = 10,
  filters?: SharedKnowledgeFileFilterParams
) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: queryKeys.files(page, limit, filters),
    queryFn: async () => {
      const response = await client.sharedKnowledgeFile.getFiles({
        query: {
          page,
          limit,
          ...filters,
        },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch shared knowledge files');
    },
  });
};

export const useSharedKnowledgeFileByIdApi = (fileId: string) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: queryKeys.fileById(fileId),
    queryFn: async () => {
      const response = await client.sharedKnowledgeFile.getFileById({
        params: { id: fileId },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch shared knowledge file');
    },
  });
};

export const useSharedKnowledgeFilesForCitationApi = () => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.filesForCitation(),
    queryFn: async () => {
      const response = await client.sharedKnowledgeFile.getFilesForCitation();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch shared knowledge files for citation');
    },
  });
};
export const useSharedKnowledgeGroupsApi = () => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.groups(),
    queryFn: async () => {
      const response = await client.sharedKnowledgeFile.getGroupsPublic();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch shared knowledge groups');
    },
  });
};

export const useCreateSharedKnowledgeGroupApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (group: CreateSharedKnowledgeGroupInput) => {
      const response = await client.sharedKnowledgeFile.createGroup({
        body: group,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create shared knowledge file group');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.allGroups(),
      });
    },
  });
};

export const useUpdateSharedKnowledgeGroupApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      group,
    }: {
      id: string;
      group: UpdateSharedKnowledgeGroupInput;
    }) => {
      const response = await client.sharedKnowledgeFile.updateGroup({
        params: { id },
        body: group,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update shared knowledge file group');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allGroups() });
      queryClient.invalidateQueries({ queryKey: queryKeys.allFiles() });
    },
  });
};

export const useDeleteSharedKnowledgeGroupApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const response = await client.sharedKnowledgeFile.deleteGroup({
        params: { id },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete shared knowledge file group');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allGroups() });
      queryClient.invalidateQueries({ queryKey: queryKeys.allFiles() });
    },
  });
};

export const useUploadSharedKnowledgeFileApi = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);
      const baseUrl = getBaseUrl();

      // For file uploads, we need to use fetch directly since ts-rest doesn't handle FormData well
      const response = await fetch(
        `${baseUrl}/admin/shared-knowledge/upload-file`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      return response.json() as Promise<SharedKnowledgeFileDto>;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allFiles() });
    },
    onError: error => {
      console.error('Upload failed:', error);
    },
  });
};

export const useUpdateSharedKnowledgeFileApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fileId,
      patch,
    }: {
      fileId: string;
      patch: PatchSharedKnowledgeFileInput;
    }) => {
      const response = await client.sharedKnowledgeFile.updateFile({
        body: {
          fileId,
          patch,
        },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update shared knowledge file');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allFiles() });
    },
  });
};

export const useDeleteSharedKnowledgeFileApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.sharedKnowledgeFile.deleteFile({
        params: { id },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete shared knowledge file');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allFiles() });
    },
  });
};

export const useDeleteManySharedKnowledgeFilesApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeleteManySharedKnowledgeFilesInput) => {
      const response = await client.sharedKnowledgeFile.deleteManyFiles({
        body: input,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete shared knowledge files');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allFiles() });
    },
  });
};

export const useDownloadSharedFileApi = (fileId: string | undefined) => {
  const { getToken } = useAuth();
  const workspaceBaseUrl = useWorkspaceBaseUrl();

  return useProtectedQuery({
    queryKey: ['download_shared_file', fileId],
    queryFn: fileId
      ? async () => {
          const token = await getToken();
          const response = await fetch(
            `${workspaceBaseUrl}/shared-knowledge/files/${fileId}/download`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Download failed');
          }

          return response.blob();
        }
      : skipToken,
  });
};

export const useBulkUploadSharedKnowledgeFilesApi = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (files: File[]) => {
      const token = await getToken();
      const formData = new FormData();

      // Append all files to FormData
      files.forEach(file => {
        formData.append('files', file);
      });

      const baseUrl = getBaseUrl();

      const response = await fetch(
        `${baseUrl}/admin/shared-knowledge/upload-files`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Bulk upload failed');
      }

      return response.json() as Promise<SharedKnowledgeFileDto[]>;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allFiles() });
    },
    onError: error => {
      console.error('Bulk upload failed:', error);
    },
  });
};

export const useBulkUpdateSharedKnowledgeFilesApi = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: BulkUpdateSharedKnowledgeFilesInput) => {
      const response = await client.sharedKnowledgeFile.bulkUpdateFiles({
        body: input,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to bulk update shared knowledge files');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allFiles() });
    },
  });
};
